import { forwardRef, ReactNode } from "react";
import { AlertCircle, Check, Image, Info, Lightbulb, Loader2, MessageSquareQuote, Palette } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * Onboarding bridges the marketing site (grotesque display type, confident
 * orange CTAs) and the app (warm, quiet card surfaces). These pieces carry
 * that shared look so every setup step reads as one sequence.
 */

export function SetupProgress({
  steps,
  current,
}: {
  steps: string[];
  current: number;
}) {
  return (
    <nav aria-label="Setup progress" className="w-full">
      <ol className="grid gap-2" style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
        {steps.map((label, index) => {
          const state = index < current ? "done" : index === current ? "current" : "upcoming";
          return (
            <li
              key={label}
              aria-current={state === "current" ? "step" : undefined}
              className="min-w-0"
            >
              <span
                aria-hidden="true"
                className={cn(
                  "block h-1 rounded-full transition-colors duration-500",
                  state === "upcoming" ? "bg-foreground/10" : "bg-brand",
                )}
              />
              <span
                className={cn(
                  "mt-2.5 flex items-center gap-1.5 truncate text-xs",
                  state === "upcoming" ? "text-muted-foreground" : "font-medium text-foreground",
                  // Phones get the one-line summary below; labels stay for assistive tech.
                  "max-sm:sr-only",
                )}
              >
                {state === "done" && <Check aria-hidden="true" className="h-3 w-3 shrink-0 text-brand-ink" />}
                {state === "done" && <span className="sr-only">Completed: </span>}
                <span className="truncate">{label}</span>
              </span>
            </li>
          );
        })}
      </ol>
      <p aria-hidden="true" className="mt-2.5 text-xs text-muted-foreground sm:hidden">
        Step {Math.min(current, steps.length - 1) + 1} of {steps.length} ·{" "}
        <span className="font-medium text-foreground">{steps[Math.min(current, steps.length - 1)]}</span>
      </p>
    </nav>
  );
}

/**
 * One treatment for everything setup needs to tell the user. Errors are red
 * and announced; partial successes are calm, because nothing is blocked.
 */
export const SetupNotice = forwardRef<
  HTMLDivElement,
  {
    tone?: "error" | "info";
    title?: ReactNode;
    children: ReactNode;
    action?: ReactNode;
    className?: string;
  }
>(function SetupNotice({ tone = "error", title, children, action, className }, ref) {
  const Icon = tone === "error" ? AlertCircle : Info;
  return (
    <div
      ref={ref}
      role={tone === "error" ? "alert" : "status"}
      className={cn(
        "flex w-full max-w-2xl gap-3 rounded-xl border px-4 py-3.5 text-sm leading-relaxed",
        tone === "error"
          ? "border-destructive/25 bg-destructive/[0.04] text-destructive"
          : "border-foreground/10 bg-foreground/[0.03] text-foreground",
        className,
      )}
    >
      <Icon
        aria-hidden="true"
        className={cn("mt-0.5 h-4 w-4 shrink-0", tone === "info" && "text-muted-foreground")}
      />
      <div className="min-w-0 flex-1">
        {title && <p className="font-semibold">{title}</p>}
        <div className={cn(title && "mt-0.5", tone === "error" ? "text-destructive/90" : "text-muted-foreground")}>
          {children}
        </div>
      </div>
      {action && <div className="shrink-0 self-center">{action}</div>}
    </div>
  );
});

/** Side card beside the website step's form. */
const sideCard = "rounded-3xl border bg-card p-6 shadow-sm sm:p-7";

export const scanStages: ReadonlyArray<readonly [string, string, LucideIcon?]> = [
  ["preparing", "Opening your website"],
  ["reading", "Reading your pages"],
  ["style", "Colors and fonts"],
  ["voice", "Business, audience and voice"],
  ["images", "Logo and photos"],
  ["saving", "Putting your brand kit together"],
];

/**
 * What a website scan picks up. At rest it previews the result; while
 * scanning it reports the server's real stages.
 */
export function BrandScanPanel({
  stage,
  findsIdeas = true,
  className,
}: {
  stage: string | null;
  /** Business setup goes on to find post ideas; a single-post handoff doesn't. */
  findsIdeas?: boolean;
  className?: string;
}) {
  const scanning = !!stage;
  const current = Math.max(0, scanStages.findIndex(([key]) => key === stage));
  return (
    <section
      aria-label={scanning ? "Website scan progress" : "What we pick up from your website"}
      className={cn(sideCard, className)}
    >
      <h2 className="onboarding-display text-xl">
        {scanning ? "Building your brand kit…" : "What we’ll pick up"}
      </h2>
      {/* The stage list only swaps icons, so announce each stage in words. */}
      <p role="status" className="sr-only">
        {scanning ? `Building your brand kit: ${scanStages[current][1]}` : ""}
      </p>
      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
        {scanning
          ? "Usually about a minute. You can switch to manual setup anytime."
          : "From your website, ready for you to review."}
      </p>
      <ol className="mt-5 space-y-1">
        {(scanning
          ? scanStages
          : ([
              ["logo", "Logo and photos", Image],
              ["style", "Colors and fonts", Palette],
              ["voice", "How you sound and who you serve", MessageSquareQuote],
              ...(findsIdeas ? [["ideas", "Content worth turning into posts", Lightbulb] as const] : []),
            ] as const)
        ).map(([key, label, IdleIcon], index) => {
          const state = !scanning ? "idle" : index < current ? "done" : index === current ? "active" : "upcoming";
          return (
            <li
              key={key}
              aria-current={state === "active" ? "step" : undefined}
              className={cn(
                "flex items-center gap-3 rounded-xl text-sm transition-colors duration-300",
                state === "idle" ? "py-1.5" : "px-3 py-2.5",
                state === "active" && "bg-muted font-medium",
                state === "upcoming" && "text-muted-foreground",
              )}
            >
              <span aria-hidden="true" className={cn("grid shrink-0 place-items-center", state === "idle" ? "h-9 w-9 rounded-xl bg-[oklch(var(--brand)/0.1)]" : "h-5 w-5")}>
                {state === "idle" && IdleIcon ? (
                  <IdleIcon className="h-4 w-4 text-brand-ink" />
                ) : state === "done" ? (
                  <span className="grid h-5 w-5 place-items-center rounded-full bg-brand">
                    <Check className="h-3 w-3 text-brand-foreground" strokeWidth={3} />
                  </span>
                ) : state === "active" ? (
                  <Loader2 className="h-4 w-4 animate-spin text-brand-ink motion-reduce:animate-none" />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-foreground/20" />
                )}
              </span>
              {label}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/**
 * A titled group on the brand review. Grouping comes from headings and space
 * alone: no card, no hairlines.
 */
export function ReviewGroup({
  id,
  title,
  hint,
  className,
  children,
}: {
  id: string;
  title: ReactNode;
  hint?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={id} className={cn("min-w-0 space-y-5", className)}>
      <div>
        <h2 id={id} className="onboarding-display text-xl">{title}</h2>
        {hint && <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{hint}</p>}
      </div>
      {children}
    </section>
  );
}
